import React from 'react';

import { ColumnsType, TableProps } from 'antd/es/table';
import styled from 'styled-components';

import { StyledTable } from 'common/Table/StyledTable';
import { SpeedToLeadEventMetric, SpeedToLeadFirstResponseRow } from 'types/recommendations';

import { formatDuration, getEventChannelLabel } from './speedToLeadOverallUtils';
import { CommunityMeta, CommunityName, SubsectionTitle } from './SpeedToLeadSection.styles';

const MetricTableWrapper = styled.div`
  border-left: 5px solid var(--lavender-purple);
  padding: 28px 12px 24px 28px;
  background: var(--white);

  &&&& .ant-table {
    overflow: hidden;
    border: 1px solid var(--lighter-gray);
    border-radius: 6px;
    background: var(--background-gray-light);
  }

  &&&& .ant-table-container {
    border-radius: 6px;
  }

  &&&& .ant-table-thead > tr > th.ant-table-cell {
    padding: 8px 12px;
    border-bottom: 0;
    background: var(--background-gray-light);
    color: var(--text-secondary);
    font-size: 12px;
    font-family: var(--font-regular);
    font-weight: normal;
    white-space: nowrap;
  }

  &&&& .ant-table-thead > tr > th.ant-table-cell::before {
    display: none;
  }

  &&&& .ant-table-tbody > tr > td.ant-table-cell {
    padding: 6px 12px;
    border-bottom: 0;
    background: var(--background-gray-light);
  }

  &&&& .ant-table-tbody > tr > td.ant-table-cell:first-child {
    color: var(--text-primary);
    font-size: 14px;
    font-family: var(--font-bold);
  }

  &&&& .ant-table-tbody > tr.primary > td,
  &&&& .ant-table-tbody > tr.primary:hover > td {
    background: var(--competitor-purple-highlight) !important;
  }

  &&&& .ant-table-tbody > tr:not(.primary):hover > td {
    background: var(--background-gray-light);
  }
`;

const MetricTitle = styled(SubsectionTitle)`
  margin-bottom: 20px;
`;

const ResponseCommunityName = styled(CommunityName)`
  font-size: 14px;
`;

const ResponseTime = styled.div`
  color: var(--text-primary);
  font-family: var(--font-bold);
`;

const ResponseChannel = styled.div`
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 12px;
`;

const SpeedToLeadTable = StyledTable as <RecordType extends object>(
  props: TableProps<RecordType>
) => React.ReactElement;

type Props = {
  getEvent: (row: SpeedToLeadFirstResponseRow) => SpeedToLeadEventMetric;
  primaryCommunityId: number | null;
  rows: SpeedToLeadFirstResponseRow[];
  title: string;
};

const FirstResponseTable = ({ getEvent, primaryCommunityId, rows, title }: Props) => {
  const rankedRows = [...rows]
    .sort((left, right) => {
      const leftSeconds = getEvent(left).elapsed_seconds ?? Number.POSITIVE_INFINITY;
      const rightSeconds = getEvent(right).elapsed_seconds ?? Number.POSITIVE_INFINITY;
      return leftSeconds - rightSeconds;
    })
    .map((row, index) => {
      const response = getEvent(row);
      return {
        ...row,
        rank: response.elapsed_seconds == null ? '-' : index + 1,
        response,
        is_primary: row.community_id === primaryCommunityId,
      };
    });

  const columns: ColumnsType<(typeof rankedRows)[number]> = [
    { title: 'Rank', dataIndex: 'rank', key: 'rank', width: 55 },
    {
      title: 'Community',
      dataIndex: 'community_name',
      key: 'community_name',
      render: (name: string, row) => (
        <div>
          <ResponseCommunityName>{name}</ResponseCommunityName>
          <CommunityMeta>{row.is_primary ? 'My Community' : 'Competitor'}</CommunityMeta>
        </div>
      ),
    },
    {
      title: 'Response Speed',
      key: 'response',
      align: 'right',
      render: (_: unknown, row) => (
        <div>
          <ResponseTime>{formatDuration(row.response.elapsed_seconds)}</ResponseTime>
          {row.response.channel && <ResponseChannel>{getEventChannelLabel(row.response)}</ResponseChannel>}
        </div>
      ),
    },
  ];

  return (
    <MetricTableWrapper>
      <MetricTitle>{title}</MetricTitle>
      <SpeedToLeadTable
        columns={columns}
        dataSource={rankedRows}
        rowKey="community_id"
        rowClassName={(row) => (row.is_primary ? 'primary' : '')}
        pagination={false}
        bordered={false}
        size="small"
      />
    </MetricTableWrapper>
  );
};

export default FirstResponseTable;
